import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ClipboardList,
  Palette,
  Code2,
  ShieldCheck,
  Rocket,
  Headphones,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import AnimatedHeading from "../../../components/animations/AnimatedHeading";


import requirementImg from "../../../Assets/Images/requirement.png";
import planningImg from "../../../Assets/Images/planning.png";
import designImg from "../../../Assets/Images/design.png";
import developmentImg from "../../../Assets/Images/development.png";
import testingImg from "../../../Assets/Images/testing.png";
import deploymentImg from "../../../Assets/Images/deployment.png";
import supportImg from "../../../Assets/Images/support.png";
 
const phases = [
  {
    id: "01",
    title: "Requirement Analysis",
    shortTitle: "Requirements",
    icon: Search,
    image: requirementImg,
    description:
      "We understand your business goals, gather requirements, define project scope, identify risks, and create a clear roadmap for successful development.",
    points: [
      "Business Discovery",
      "Requirement Gathering",
      "Scope Definition",
      "Risk Analysis",
    ],
  },
  {
    id: "02",
    title: "Planning & Architecture",
    shortTitle: "Planning",
    icon: ClipboardList,
    image: planningImg,
    description:
      "We create a scalable technical architecture, select the right technology stack, define milestones, and establish a strong foundation for long-term growth.",
    points: [
      "Architecture Design",
      "Tech Stack",
      "Sprint Planning",
      "Database Design",
    ],
  },
  {
    id: "03",
    title: "UI / UX Design",
    shortTitle: "Design",
    icon: Palette,
    image: designImg,
    description:
      "We transform ideas into intuitive digital experiences through user research, wireframes, prototypes, and responsive interface design.",
    points: [
      "Wireframes",
      "Interactive Prototype",
      "Responsive UI",
      "User Experience",
    ],
  },
  {
    id: "04",
    title: "Development",
    shortTitle: "Development",
    icon: Code2,
    image: developmentImg,
    description:
      "Our engineers build secure, scalable, and high-performance applications using modern technologies, clean architecture, and industry best practices.",
    points: [
      "Frontend",
      "Backend",
      "API Integration",
      "Code Quality",
    ],
  },
  {
    id: "05",
    title: "Testing & Quality Assurance",
    shortTitle: "Testing",
    icon: ShieldCheck,
    image: testingImg,
    description:
      "Every feature is rigorously tested to eliminate defects, validate functionality, improve performance, and ensure a reliable product.",
    points: [
      "Automation Testing",
      "Manual Testing",
      "Performance Test",
      "Bug Resolution",
    ],
  },
  {
    id: "06",
    title: "Deployment",
    shortTitle: "Deployment",
    icon: Rocket,
    image: deploymentImg,
    description:
      "We deliver a smooth production launch through secure cloud deployment, CI/CD automation, infrastructure optimization, and monitoring.",
    points: [
      "Cloud Deployment",
      "CI/CD Pipeline",
      "Monitoring",
      "Security Setup",
    ],
  },
  {
    id: "07",
    title: "Maintenance & Support",
    shortTitle: "Support",
    icon: Headphones,
    image: supportImg,
    description:
      "Our support continues after launch with monitoring, optimization, security updates, enhancements, and ongoing technical assistance.",
    points: [
      "Monitoring",
      "Optimization",
      "Security Updates",
      "24/7 Support",
    ],
  },
];


export default function Working() {
  const [activeStep, setActiveStep] = useState(0);
  const currentPhase = phases[activeStep];


  const handleStepChange = (index) => {
    setActiveStep(index);
  };

  const handlePrevious = () => {
    setActiveStep((prev) =>
      prev === 0 ? phases.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setActiveStep((prev) =>
      prev === phases.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section
      className="
        theme-bg-primary
        relative
        w-full
        overflow-hidden
        px-3
        py-6
        font-saira
        transition-colors
        duration-300
        min-[375px]:px-4
        min-[375px]:py-7
        sm:px-5
        sm:py-8
        md:px-7
        md:py-9
        lg:px-10
        lg:py-10
        xl:px-12
        xl:py-12
      "
    >

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-16
          h-[180px]
          w-[180px]
          rounded-full
          bg-primaryColor/[0.055]
          blur-[90px]
          sm:-left-40
          sm:h-[260px]
          sm:w-[260px]
          sm:blur-[110px]
          lg:h-[380px]
          lg:w-[380px]
          lg:blur-[130px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-16
          h-[180px]
          w-[180px]
          rounded-full
          bg-primaryColor/[0.045]
          blur-[90px]
          sm:-right-40
          sm:h-[260px]
          sm:w-[260px]
          sm:blur-[110px]
          lg:h-[380px]
          lg:w-[380px]
          lg:blur-[130px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          opacity-[0.018]
        "
      >
        <div
          className="h-full w-full"
          style={{
            backgroundImage: `
              linear-gradient(
                var(--grid-line-color, rgba(255,255,255,0.14)) 1px,
                transparent 1px
              ),
              linear-gradient(
                90deg,
                var(--grid-line-color, rgba(255,255,255,0.14)) 1px,
                transparent 1px
              )
            `,
            backgroundSize: "55px 55px",
          }}
        />
      </div>

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-[1500px]
        "
      >

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.5,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-7
            max-w-3xl
            text-center
            sm:mb-8
            md:mb-10
          "
        >

          <p
            className="
              brand-color
              text-[8px]
              font-bold
              uppercase
              tracking-[0.22em]
              min-[375px]:text-[9px]
              sm:text-[10px]
              md:text-xs
            "
          >
            [ Software Development Lifecycle ]
          </p>

          <AnimatedHeading
            as="h2"
            parts={["From Idea to Successful Software"]}
            className="
              theme-text-primary
              mt-3
              text-[1.65rem]
              font-semibold
              leading-[1.08]
              tracking-[-0.025em]
              min-[375px]:text-[1.8rem]
              sm:mt-4
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
          />

          <p
            className="
              theme-text-secondary
              mx-auto
              mt-3
              max-w-2xl
              px-1
              text-[10px]
              leading-[1.65]
              min-[375px]:text-[11px]
              sm:text-[13px]
              sm:leading-6
              md:text-[14px]
              lg:text-[15px]
            "
          >
            Our structured software development process transforms ideas into
            secure, scalable, and high-performance digital solutions through
            strategy, design, engineering, testing, and continuous support.
          </p>
        </motion.div>

        <div
          className="
            relative
            mb-5
            w-full
            sm:mb-6
            md:mb-7
          "
        >
          <div
            className="
              relative
              z-10
              flex
              w-full
              overflow-x-auto
              overflow-y-hidden
              px-0.5
              pb-3
              scrollbar-hide
              lg:grid
              lg:grid-cols-7
              lg:overflow-visible
              lg:pb-1
            "
          >
            {phases.map((phase, index) => {
              const Icon = phase.icon;

              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              const isLast = index === phases.length - 1;

              const stepIconClass = isActive
                ? `
                  brand-border
                  brand-bg
                  text-black
                  shadow-[0_0_24px_rgba(230,112,0,0.28)]
                `
                : isCompleted
                ? `
                  border-[var(--brand-primary)]
                  bg-[var(--bg-primary)]
                  brand-color
                `
                : `
                  theme-border
                  theme-bg-primary
                  theme-text-muted
                  group-hover:border-[var(--brand-primary)]
                  group-hover:text-[var(--brand-primary)]
                `;

              const stepNumberClass = isActive
                ? "brand-color"
                : isCompleted
                ? "text-[color:var(--brand-primary)] opacity-65"
                : "theme-text-muted";

              const stepTitleClass = isActive
                ? "theme-text-primary"
                : isCompleted
                ? "theme-text-secondary"
                : "theme-text-muted group-hover:text-[var(--text-secondary)]";

              return (
                <button
                  key={phase.id}
                  type="button"
                  onClick={() => handleStepChange(index)}
                  className="
                    group
                    relative
                    flex
                    h-[118px]
                    min-w-[96px]
                    shrink-0
                    flex-1
                    flex-col
                    items-center
                    text-center
                    outline-none
                    min-[375px]:min-w-[104px]
                    sm:h-[128px]
                    sm:min-w-[112px]
                    md:h-[136px]
                    md:min-w-[125px]
                    lg:h-[142px]
                    lg:min-w-0
                  "
                >
                  
                  {!isLast && (
                    <span
                      className="
                        theme-border
                        pointer-events-none
                        absolute
                        left-1/2
                        top-[24px]
                        z-0
                        h-px
                        w-full
                        border-t
                        sm:top-[27px]
                        md:top-[28px]
                        lg:top-[28px]
                      "
                    />
                  )}

                  {!isLast && (
                    <motion.span
                      className="
                        pointer-events-none
                        absolute
                        left-1/2
                        top-[24px]
                        z-[1]
                        h-[2px]
                        origin-left
                        bg-[var(--brand-primary)]
                        sm:top-[27px]
                        md:top-[28px]
                        lg:top-[28px]
                      "
                      initial={false}
                      animate={{
                        scaleX: index < activeStep ? 1 : 0,
                        opacity: index < activeStep ? 1 : 0,
                      }}
                      transition={{
                        duration: 0.35,
                        ease: "easeInOut",
                      }}
                      style={{
                        width: "100%",
                      }}
                    />
                  )}

                  <div
                    className={`
                      relative
                      z-20
                      flex
                      h-12
                      w-12
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      border
                      transition-all
                      duration-300
                      min-[375px]:h-[50px]
                      min-[375px]:w-[50px]
                      sm:h-[54px]
                      sm:w-[54px]
                      md:h-[56px]
                      md:w-[56px]
                      ${stepIconClass}
                    `}
                  >
                    <Icon
                      size={18}
                      strokeWidth={1.8}
                      className="
                        sm:h-5
                        sm:w-5
                        md:h-[21px]
                        md:w-[21px]
                      "
                    />

                    {isActive && (
                      <motion.span
                        layoutId="activeLifecycleGlow"
                        className="
                          pointer-events-none
                          absolute
                          -inset-1
                          -z-10
                          rounded-full
                          bg-primaryColor/20
                          blur-md
                        "
                      />
                    )}
                  </div>

                  <span
                    className={`
                      relative
                      z-20
                      mt-2
                      text-[7px]
                      font-semibold
                      uppercase
                      tracking-[0.14em]
                      transition-colors
                      min-[375px]:text-[8px]
                      sm:mt-2.5
                      sm:text-[9px]
                      md:text-[10px]
                      ${stepNumberClass}
                    `}
                  >
                    Step {phase.id}
                  </span>

                  <span
                    className={`
                      relative
                      z-20
                      mt-1
                      max-w-[100px]
                      whitespace-nowrap
                      overflow-hidden
                      text-ellipsis
                      text-[8px]
                      font-medium
                      transition-colors
                      min-[375px]:text-[9px]
                      sm:max-w-[110px]
                      sm:text-[10px]
                      md:max-w-[120px]
                      md:text-[11px]
                      lg:max-w-[130px]
                      lg:text-[12px]
                      ${stepTitleClass}
                    `}
                  >
                    {phase.shortTitle}
                  </span>

                  <motion.span
                    initial={false}
                    animate={{
                      width: isActive ? 28 : 0,
                      opacity: isActive ? 1 : 0,
                    }}
                    transition={{
                      duration: 0.3,
                    }}
                    className="
                      relative
                      z-20
                      mt-2
                      h-[2px]
                      rounded-full
                      bg-[var(--brand-primary)]
                      sm:w-9
                      md:w-10
                    "
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="
            theme-card
            theme-border
            w-full
            overflow-hidden
            rounded-[16px]
            border
            shadow-[0_18px_60px_var(--shadow-color)]
            sm:rounded-[20px]
            md:rounded-[22px]
          "
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPhase.id}
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                y: -8,
              }}
              transition={{
                duration: 0.32,
                ease: "easeOut",
              }}
              className="
                grid
                grid-cols-1
                lg:grid-cols-[1.1fr_1fr]
              "
            >

              <div
                className="
                  theme-card
                  flex
                  min-w-0
                  flex-col
                  justify-center
                  p-4
                  min-[375px]:p-5
                  sm:p-6
                  md:p-7
                  lg:p-8
                  xl:p-9
                "
              >

                <div className="flex items-center gap-2">
                  <span
                    className="
                      brand-color
                      text-[12px]
                      font-bold
                      uppercase
                      tracking-[0.2em]
                      min-[375px]:text-[13px]
                      sm:text-[15px]
                    "
                  >
                    Step {currentPhase.id}
                  </span>
                </div>

                <h3
                  className="
                    theme-text-primary
                    mt-2
                    max-w-xl
                    text-[1.55rem]
                    font-semibold
                    leading-[1.1]
                    tracking-[-0.025em]
                    min-[375px]:text-[1.7rem]
                    sm:text-2xl
                    md:text-[32px]
                    lg:text-[38px]
                    xl:text-[40px]
                  "
                >
                  {currentPhase.title}
                </h3>

                <p
                  className="
                    theme-text-secondary
                    mt-3
                    max-w-2xl
                    text-[10px]
                    leading-[1.7]
                    min-[375px]:text-[11px]
                    sm:text-[13px]
                    sm:leading-6
                    md:text-sm
                    lg:text-[15px]
                    lg:leading-6
                  "
                >
                  {currentPhase.description}
                </p>

                <div
                  className="
                    mt-5
                    grid
                    grid-cols-2
                    gap-2
                    sm:mt-6
                    sm:gap-2.5
                  "
                >
                  {currentPhase.points.map((point, index) => (
                    <motion.div
                      key={point}
                      initial={{
                        opacity: 0,
                        x: -5,
                      }}
                      animate={{
                        opacity: 1,
                        x: 0,
                      }}
                      transition={{
                        delay: 0.06 + index * 0.05,
                        duration: 0.25,
                      }}
                      className="
                        theme-border
                        flex
                        min-w-0
                        items-center
                        gap-1.5
                        rounded-lg
                        border
                        bg-[var(--bg-secondary)]
                        px-2
                        py-2
                        transition-all
                        duration-300
                        hover:border-[var(--brand-primary)]
                        hover:bg-[var(--bg-tertiary)]
                        min-[375px]:gap-2
                        min-[375px]:px-2.5
                        sm:px-3
                        sm:py-2.5
                      "
                    >
                      <CheckCircle2
                        size={13}
                        strokeWidth={1.8}
                        className="
                          brand-color
                          shrink-0
                          sm:h-4
                          sm:w-4
                        "
                      />

                      <span
                        className="
                          theme-text-secondary
                          min-w-0
                          truncate
                          text-[8px]
                          font-medium
                          min-[375px]:text-[9px]
                          sm:text-[11px]
                          md:text-[13px]
                          lg:text-[14px]
                        "
                      >
                        {point}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <div
                  className="
                    theme-border
                    mt-5
                    flex
                    items-center
                    justify-between
                    gap-3
                    border-t
                    pt-4
                    sm:mt-6
                    sm:pt-5
                  "
                >

                  <div className="min-w-0">
                    <p
                      className="
                        theme-text-muted
                        text-[7px]
                        font-semibold
                        uppercase
                        tracking-[0.15em]
                        sm:text-[10px]
                      "
                    >
                      Development Lifecycle
                    </p>

                    <p
                      className="
                        theme-text-secondary
                        mt-0.5
                        text-[10px]
                        sm:text-xs
                      "
                    >
                      {currentPhase.id} /{" "}
                      {String(phases.length).padStart(2, "0")}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-1.5">

                    <button
                      type="button"
                      onClick={handlePrevious}
                      aria-label="Previous phase"
                      className="
                        theme-border
                        theme-text-secondary
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        border
                        bg-[var(--bg-tertiary)]
                        transition-all
                        duration-300
                        hover:border-[var(--brand-primary)]
                        hover:bg-[var(--brand-primary)]
                        hover:text-black
                        sm:h-9
                        sm:w-9
                        md:h-10
                        md:w-10
                      "
                    >
                      <ArrowLeft
                        size={15}
                        className="
                          sm:h-4
                          sm:w-4
                        "
                      />
                    </button>

                    <button
                      type="button"
                      onClick={handleNext}
                      aria-label="Next phase"
                      className="
                        brand-border
                        brand-bg
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        border
                        text-black
                        transition-all
                        duration-300
                        hover:border-[var(--text-primary)]
                        hover:bg-[var(--text-primary)]
                        sm:h-9
                        sm:w-9
                        md:h-10
                        md:w-10
                      "
                    >
                      <ArrowRight
                        size={15}
                        className="
                          sm:h-4
                          sm:w-4
                        "
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="
                  theme-border
                  relative
                  h-[190px]
                  min-h-0
                  overflow-hidden
                  border-t
                  min-[375px]:h-[205px]
                  sm:h-[240px]
                  md:h-[280px]
                  lg:h-auto
                  lg:min-h-[370px]
                  lg:border-l
                  lg:border-t-0
                  xl:min-h-[390px]
                "
              >
                <motion.img
                  key={currentPhase.image}
                  initial={{
                    scale: 1.05,
                    opacity: 0,
                  }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                  }}
                  transition={{
                    duration: 0.55,
                    ease: "easeOut",
                  }}
                  src={currentPhase.image}
                  alt={currentPhase.title}
                  className="
                    absolute
                    inset-0
                    h-full
                    w-full
                    object-cover
                  "
                />

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-black/20
                  "
                />

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-gradient-to-r
                    from-black/60
                    via-black/10
                    to-transparent
                  "
                />

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-x-0
                    bottom-0
                    h-1/2
                    bg-gradient-to-t
                    from-black/75
                    to-transparent
                  "
                />

                <div
                  className="
                    absolute
                    bottom-3
                    left-3
                    rounded-full
                    border
                    border-white/10
                    bg-black/35
                    px-2.5
                    py-1
                    backdrop-blur-md
                    sm:bottom-4
                    sm:left-4
                    sm:px-3
                    sm:py-1.5
                  "
                >
                  <span
                    className="
                      text-[7px]
                      font-semibold
                      uppercase
                      tracking-[0.18em]
                      text-white/70
                      sm:text-[8px]
                    "
                  >
                    {currentPhase.shortTitle}
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div
          className="
            mt-5
            flex
            items-center
            justify-center
            gap-1.5
            lg:hidden
          "
        >
          {phases.map((phase, index) => {
            const isActive = index === activeStep;

            return (
              <button
                key={phase.id}
                type="button"
                onClick={() => handleStepChange(index)}
                aria-label={`Go to ${phase.title}`}
                className={`
                  h-1
                  rounded-full
                  transition-all
                  duration-300
                  ${
                    isActive
                      ? "w-7 bg-[var(--brand-primary)]"
                      : "w-1.5 bg-[var(--text-muted)] opacity-40"
                  }
                `}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}